import React, { useState, useEffect } from 'react';
import { fetchData, fetchFleetSummary, fetchCurrentSessionState, fetchDeviceTags } from '../api/restapi';
import DeviceTimeline from './DeviceTimeline';
import SvgPieChart from '../reports/SvgPieChart';
import RdQuickStart from '../reports/RdQuickStart';
import RtQuickStart from '../reports/RtQuickStart';
import StartRemoteDebugTab from './StartRemoteDebug';
import RTStartSession from './RTStartSession';
import NdRdViewSessions from '../reports/NdRdViewSessions';
import NdRtViewSessions from '../reports/NdRtViewSessions';
import '../assets/FleetAnalytics.css';
import '../assets/AllTabsCommon.css';
import '../assets/NewDiagnostics.css';

const PIE_COLORS = ['#3498db', '#2ecc71', '#f1c40f', '#e74c3c', '#9b59b6', '#1abc9c', '#e67e22', '#e91e63'];

const CATEGORY_LABELS = {
  oem: 'OEM',
  make: 'Make',
  model: 'Model',
  chipset: 'Chipset',
};

function NewDiagnostics({ userInfo, role, userGroups = [], setSelectedTab, setPrefilledSystemId, ndStartDevice, setNdStartDevice }) {
  // ── SSE-sourced state (connected/disconnected + deviceSplit for pie) ──
  const [sseData, setSseData] = useState(null);
  const [sseConnected, setSseConnected] = useState(false);

  // ── Fleet summary (onboarded count) ──
  const [onboardedCount, setOnboardedCount] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(true);

  // ── Device table ──
  const [devices, setDevices] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [allTags, setAllTags] = useState([]);
  const [devicesLoading, setDevicesLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTag, setSelectedTag] = useState('');
  const [mqttSort, setMqttSort] = useState(null); // null | 'connected' | 'disconnected'
  const [currentPage, setCurrentPage] = useState(1);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const rowsPerPage = 10;

  // ── Category filter for pie ──
  const [selectedCategory, setSelectedCategory] = useState('oem');

  // ── Timeline modal ──
  const [showTimelineModal, setShowTimelineModal] = useState(false);
  const [timelineSystemId, setTimelineSystemId] = useState('');

  // ── Navigation state ──
  // L1: 'fleet' | 'rd' | 'rt'
  // L2: 'start' | 'view'
  const [ndL1, setNdL1] = useState('fleet');
  const [ndRdL2, setNdRdL2] = useState('start');
  const [ndRtL2, setNdRtL2] = useState('start');

  // systemId carried into Start sub-tabs
  const [rdSystemId, setRdSystemId] = useState('');
  const [rtSystemId, setRtSystemId] = useState('');

  // Per-device active session map: { [systemId]: { rd: bool, rt: bool } }
  const [deviceSessionStatus, setDeviceSessionStatus] = useState({});

  // ── SSE connection ──
  useEffect(() => {
    if (!userInfo) return;

    const sseUrl = `${process.env.REACT_APP_WBC_API_BASE_URL}/api/dashboard/stream?userEmail=${userInfo}`;
    const es = new EventSource(sseUrl);

    const handleDashboardUpdate = (event) => {
      try {
        const data = JSON.parse(event.data);
        setSseData(data);
        setSseConnected(true);
      } catch (err) {
        console.error('NewDiagnostics SSE parse error:', err);
      }
    };

    es.onopen = () => setSseConnected(true);
    es.addEventListener('dashboard-update', handleDashboardUpdate);
    es.addEventListener('dashboard-data', handleDashboardUpdate);
    es.onerror = () => setSseConnected(false);

    return () => {
      es.close();
      setSseConnected(false);
    };
  }, [userInfo]);

  // ── Fleet summary (onboarded count) ──
  useEffect(() => {
    setSummaryLoading(true);
    fetchFleetSummary('7d')
      .then((res) => {
        if (res && !res.errorMessage) {
          const fleetStats = res?.data?.fleet_stats || res?.fleet_stats || null;
          setOnboardedCount(fleetStats?.devices_onboarded ?? null);
        }
      })
      .catch(() => setOnboardedCount(null))
      .finally(() => setSummaryLoading(false));
  }, []);

  // ── Device table fetch ──
  const loadDevices = async (page, search, tag, sort) => {
    setDevicesLoading(true);
    try {
      let url = `/api/v1/devices?userRole=Admin&userEmailId=${userInfo}&page=${page}&size=${rowsPerPage}`;
      if (search && search.trim()) url += `&search=${encodeURIComponent(search.trim())}`;
      if (tag) url += `&tag=${encodeURIComponent(tag)}`;
      if (sort) url += `&sortBy=clientStatus&sortDir=${sort === 'connected' ? 'DESC' : 'ASC'}`;
      const data = await fetchData(url);
      if (data.statusCode === 200) {
        setDevices(data.registeredClients || []);
        setPagination(data.pagination || null);
      }
    } catch (err) {
      console.error('NewDiagnostics device fetch error:', err);
    } finally {
      setDevicesLoading(false);
    }
  };

  // Fetch distinct device tags once on mount for the dropdown
  useEffect(() => {
    if (userInfo) {
      fetchDeviceTags().then(tags => setAllTags(Array.isArray(tags) ? tags : []));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userInfo]);

  // Debounce search: wait 400ms after the user stops typing before refetching
  const [debouncedSearch, setDebouncedSearch] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchTerm), 400);
    return () => clearTimeout(t);
  }, [searchTerm]);

  // Refetch whenever page, debounced search, tag, or sort changes
  useEffect(() => {
    if (userInfo) {
      loadDevices(currentPage, debouncedSearch, selectedTag, mqttSort);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userInfo, currentPage, debouncedSearch, selectedTag, mqttSort]);

  // ── Search filter ──
  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  // ── Tag filter ──
  const handleTagChange = (e) => {
    setSelectedTag(e.target.value);
    setCurrentPage(1);
  };

  // ── MQTT sort cycle: null → connected → disconnected → null ──
  const handleMqttSort = () => {
    setMqttSort(mqttSort === null ? 'connected' : mqttSort === 'connected' ? 'disconnected' : null);
    setCurrentPage(1);
  };

  // ── Refresh ──
  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      let url = `/api/v1/devices?userRole=Admin&userEmailId=${userInfo}&page=${currentPage}&size=${rowsPerPage}`;
      if (debouncedSearch && debouncedSearch.trim()) url += `&search=${encodeURIComponent(debouncedSearch.trim())}`;
      if (selectedTag) url += `&tag=${encodeURIComponent(selectedTag)}`;
      if (mqttSort) url += `&sortBy=clientStatus&sortDir=${mqttSort === 'connected' ? 'DESC' : 'ASC'}`;
      const data = await fetchData(url);
      if (data.statusCode === 200) {
        setDevices(data.registeredClients || []);
        setPagination(data.pagination || null);
      }
    } catch (err) {
      console.error('NewDiagnostics refresh error:', err);
    } finally {
      setIsRefreshing(false);
    }
  };

  // ── Deep Dive → Overview tab / Device sub-tab ──
  const handleDeepDive = (systemId) => {    if (setPrefilledSystemId) setPrefilledSystemId(systemId);   // bare string, not an object
    if (setSelectedTab) setSelectedTab('Overview');
  };

  // ── Start RD → switch to RD > Start with device pre-filled ──
  const handleStartRd = (systemId) => {
    setRdSystemId(systemId);
    setNdL1('rd');
    setNdRdL2('start');
  };

  // ── Start RT → switch to RT > Start with device pre-filled ──
  const handleStartRt = (systemId) => {
    setRtSystemId(systemId);
    setNdL1('rt');
    setNdRtL2('start');
  };

  // ── Consume cross-tab navigation from Device sub-tab links ──
  useEffect(() => {
    if (!ndStartDevice) return;
    const { systemId: devId, target, l2 } = ndStartDevice;
    if (target === 'rt') {
      setNdL1('rt');
      if (l2 === 'view') {
        if (devId) setRtSystemId(devId);
        setNdRtL2('view');
      } else {
        setRtSystemId(devId || '');
        setNdRtL2('start');
      }
    } else {
      setNdL1('rd');
      if (l2 === 'view') {
        if (devId) setRdSystemId(devId);
        setNdRdL2('view');
      } else {
        setRdSystemId(devId || '');
        setNdRdL2('start');
      }
    }
    if (setNdStartDevice) setNdStartDevice(null);
  }, [ndStartDevice]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Pagination ──
  const totalPages = pagination?.totalPages ?? 1;

  // ── Fetch session status for devices on the current page ──
  useEffect(() => {
    if (!devices || devices.length === 0) return;
    const timestamp = new Date().toISOString();
    devices.forEach((device) => {
      fetchCurrentSessionState({ systemId: device.systemId, timestamp })
        .then((res) => {
          if (res && !res.errorMessage) {
            setDeviceSessionStatus((prev) => ({
              ...prev,
              [device.systemId]: {
                rd: res.rdSession === 'ACTIVE',
                rt: res.rtSession === 'ACTIVE',
              },
            }));
          }
        })
        .catch(() => {});
    });
  }, [devices]); // eslint-disable-line react-hooks/exhaustive-deps

  const renderPageNumbers = () => {
    const maxPagesToShow = 6;
    let startPage = Math.max(1, currentPage - Math.floor(maxPagesToShow / 2));
    let endPage = startPage + maxPagesToShow - 1;
    if (endPage > totalPages) {
      endPage = totalPages;
      startPage = Math.max(1, endPage - maxPagesToShow + 1);
    }
    const pages = [];
    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <button
          key={i}
          onClick={() => setCurrentPage(i)}
          className={currentPage === i ? 'active' : ''}
        >
          {i}
        </button>
      );
    }
    return (
      <>
        <button onClick={() => setCurrentPage(1)}>&lt;&lt;</button>
        {pages}
        <button onClick={() => setCurrentPage(totalPages)}>&gt;&gt;</button>
      </>
    );
  };

  // ── Pie chart data ──
  const getPieData = () => {
    if (!sseData || !sseData.deviceSplit) return null;
    const categoryData = sseData.deviceSplit[selectedCategory];
    if (!categoryData) return null;
    const labels = Object.keys(categoryData);
    const data = Object.values(categoryData);
    const colors = PIE_COLORS.slice(0, labels.length);
    return { labels, data, colors };
  };

  const pieData = getPieData();
  const connectedCount = sseData?.deviceStatus?.connectedCount ?? null;
  const disconnectedCount = sseData?.deviceStatus?.disconnectedCount ?? null;

  const formatCount = (val) => (val !== null ? val.toLocaleString() : '—');

  const mqttSortIcon = mqttSort === 'connected' ? ' ↑' : mqttSort === 'disconnected' ? ' ↓' : ' ⇅';

  return (
    <div className="fho-dashboard nd-page">

      {/* ══════════════════════════════════════════════
          LEVEL-1 TAB NAV
      ══════════════════════════════════════════════ */}
      <div className="analytics-tabs">
        <div
          className={`analytics-tab${ndL1 === 'fleet' ? ' active' : ''}`}
          onClick={() => { setNdL1('fleet'); setRdSystemId(''); setRtSystemId(''); }}
        >
          📡 Fleet Overview
        </div>
        <div
          className={`analytics-tab${ndL1 === 'rd' ? ' active' : ''}`}
          onClick={() => { setNdL1('rd'); setRdSystemId(''); }}
        >
          🔧 Remote Diagnostics
        </div>
        <div
          className={`analytics-tab${ndL1 === 'rt' ? ' active' : ''}`}
          onClick={() => { setNdL1('rt'); setRtSystemId(''); }}
        >
          📡 Real Time
        </div>
      </div>

      {/* ══════════════════════════════════════════════
          FLEET OVERVIEW
      ══════════════════════════════════════════════ */}
      {ndL1 === 'fleet' && <div>

        {/* SECTION 1 — KPI CARDS */}
        <div className="sec-hdr">
          <div className="sec-icon green">📡</div>
          <h2>Fleet Overview</h2>
          <div className="line" />
        </div>

        <div className="grid-3" style={{ marginBottom: 32 }}>
          <div className="kpi-card kpi-purple">
            <div className="kpi-label">Onboarded Devices</div>
            <div className="kpi-value">
              {summaryLoading ? '…' : formatCount(onboardedCount)}
            </div>
          </div>

          <div className="kpi-card kpi-green">
            <div className="kpi-label">
              {sseConnected && <span className="nd-live-dot nd-live-dot-inline" />}
              Connected Devices
            </div>
            <div className="kpi-value">
              {formatCount(connectedCount)}
            </div>
          </div>

          <div className="kpi-card kpi-red">
            <div className="kpi-label">
              {sseConnected && <span className="nd-live-dot nd-live-dot-inline" />}
              Disconnected Devices
            </div>
            <div className="kpi-value">
              {formatCount(disconnectedCount)}
            </div>
          </div>
        </div>

        {/* SECTION 2 — DEVICE CATEGORIES PIE */}
        <div className="sec-hdr">
          <div className="sec-icon blue">🥧</div>
          <h2>
            {sseConnected && <span className="nd-live-dot nd-live-dot-inline" />}
            Device Distribution
          </h2>
          <div className="line" />
        </div>

        <div className="nd-category-bar">
          {Object.entries(CATEGORY_LABELS).map(([key, label]) => (
            <button
              key={key}
              className={`filter-chip${selectedCategory === key ? ' active' : ''}`}
              onClick={() => setSelectedCategory(key)}
            >
              {label}
            </button>
          ))}
        </div>

        <div className="nd-pie-section">
          {pieData ? (
            <SvgPieChart
              labels={pieData.labels}
              values={pieData.data}
              colors={pieData.colors}
            />
          ) : (
            <div className="nd-no-data">
              {sseData ? 'No category data available.' : 'Waiting for live data…'}
            </div>
          )}
        </div>

        {/* SECTION 3 — DEVICE TABLE */}
        <div className="sec-hdr" style={{ marginTop: 36 }}>
          <div className="sec-icon orange">📋</div>
          <h2>Devices</h2>
          <div className="line" />
        </div>

        {/* Search + filter + refresh bar */}
        <div className="nd-table-toolbar">
          <div className="nd-search-bar">
            <span className="nd-search-icon">🔍</span>
            <input
              type="text"
              className="nd-search-input"
              placeholder="Search by System ID…"
              value={searchTerm}
              onChange={handleSearch}
            />
            {searchTerm && (
              <button className="nd-search-clear" onClick={() => setSearchTerm('')}>✕</button>
            )}
          </div>
          <div className="nd-tag-select-wrap">
            <select
              className="nd-tag-select"
              value={selectedTag}
              onChange={handleTagChange}
            >
              <option value="">All Tags</option>
              {allTags.map(tag => (
                <option key={tag} value={tag}>{tag}</option>
              ))}
            </select>
          </div>
          <button
            onClick={handleRefresh}
            disabled={isRefreshing}
            className="nd-refresh-btn"
            title="Refresh"
          >
            {isRefreshing ? '⏳' : '↻'}
          </button>
        </div>

        {devicesLoading ? (
          <div className="nd-no-data">Loading devices…</div>
        ) : (
          <>
            <div className="boot-kpi-table-container">
              <div className="boot-kpi-table-wrapper">
                <table className="boot-kpi-table">
                  <thead>
                    <tr>
                      <th className="boot-kpi-table-header-kpi" style={{ width: '22%' }}>System ID</th>
                      <th className="boot-kpi-table-header" style={{ width: '18%' }}>Device Tag</th>
                      <th
                        className={`boot-kpi-table-header nd-sortable-header${mqttSort ? ' nd-sort-active' : ''}`}
                        onClick={handleMqttSort}
                        title="Sort by connection status"
                        style={{ width: '14%' }}
                      >
                        Connection Status<span className="nd-sort-icon">{mqttSortIcon}</span>
                      </th>
                      <th className="boot-kpi-table-header" style={{ width: '12%' }}>Timeline</th>
                      <th className="boot-kpi-table-header" style={{ width: '12%' }}>Remote Diagnostics</th>
                      <th className="boot-kpi-table-header" style={{ width: '12%' }}>Real Time</th>
                      <th className="boot-kpi-table-header" style={{ width: '10%' }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {devices.length === 0 ? (
                      <tr>
                        <td colSpan={6} style={{ textAlign: 'center', padding: '20px', color: '#64748b' }}>
                          No devices found.
                        </td>
                      </tr>
                    ) : (
                      devices.map((device) => {
                        const isConnected = device.clientStatus === 'CONNECTED';
                        return (
                          <tr key={device.systemId}>
                            <td className="boot-kpi-table-cell-kpi nd-system-id" title={device.systemId}>{device.systemId}</td>

                            <td className="boot-kpi-table-cell" style={{ color: '#64748b' }}>
                              {device.deviceTag || '—'}
                            </td>

                            <td className="boot-kpi-table-cell">
                              <span className={isConnected ? 'status-connected' : 'status-offline'}>
                                {device.clientStatus || 'UNKNOWN'}
                              </span>
                            </td>

                            <td className="boot-kpi-table-cell">
                              <button
                                className="add-criteria-button"
                                onClick={() => {
                                  setTimelineSystemId(device.systemId);
                                  setShowTimelineModal(true);
                                }}
                              >
                                View
                              </button>
                            </td>

                            <td className="boot-kpi-table-cell">
                              {deviceSessionStatus[device.systemId] === undefined ? (
                                <span className="nd-start-rd-link" title="Loading…">🔧</span>
                              ) : deviceSessionStatus[device.systemId].rd ? (
                                <button
                                  className="add-criteria-button"
                                  onClick={() => { if (device.systemId) setRdSystemId(device.systemId); setNdL1('rd'); setNdRdL2('view'); }}
                                  title="View active RD session"
                                >
                                  View
                                </button>
                              ) : (
                                <button
                                  className="add-criteria-button"
                                  onClick={() => handleStartRd(device.systemId)}
                                  title="Start Remote Diagnostics"
                                >
                                  Start
                                </button>
                              )}
                            </td>

                            <td className="boot-kpi-table-cell">
                              {deviceSessionStatus[device.systemId] === undefined ? (
                                <span className="nd-start-rd-link" title="Loading…">📡</span>
                              ) : deviceSessionStatus[device.systemId].rt ? (
                                <button
                                  className="add-criteria-button"
                                  onClick={() => { if (device.systemId) setRtSystemId(device.systemId); setNdL1('rt'); setNdRtL2('view'); }}
                                  title="View active RT session"
                                >
                                  View
                                </button>
                              ) : (
                                <button
                                  className="add-criteria-button"
                                  onClick={() => handleStartRt(device.systemId)}
                                  title="Start Real Time"
                                >
                                  Start
                                </button>
                              )}
                            </td>

                            <td className="boot-kpi-table-cell">
                              <span
                                className="nd-deep-dive-link"
                                onClick={() => handleDeepDive(device.systemId)}
                              >
                                Deep Dive →
                              </span>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {totalPages > 1 && (
              <div className="pagination-controls">{renderPageNumbers()}</div>
            )}
          </>
        )}

        {/* ── Timeline modal ── */}
        {showTimelineModal && (
          <div className="modal-overlay" onClick={() => setShowTimelineModal(false)}>
            <div
              className="modal-content timeline-modal"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="modal-header">
                <h2>Device Timeline — {timelineSystemId}</h2>
                <button
                  className="modal-close-button"
                  onClick={() => setShowTimelineModal(false)}
                >
                  ✕
                </button>
              </div>
              <div className="modal-body">
                <DeviceTimeline prefilledSystemId={timelineSystemId} />
              </div>
            </div>
          </div>
        )}

      </div>}{/* end Fleet Overview */}

      {/* ══════════════════════════════════════════════
          RD — Level-2 nav + Start / View
      ══════════════════════════════════════════════ */}
      {ndL1 === 'rd' && <div>
        <div className="nd-l2-bar">
          <button
            className={`nd-l2-btn${ndRdL2 === 'start' ? ' active' : ''}`}
            onClick={() => { setNdRdL2('start'); setRdSystemId(''); }}
          >
            Start
          </button>
          <button
            className={`nd-l2-btn${ndRdL2 === 'view' ? ' active' : ''}`}
            onClick={() => { setNdRdL2('view'); setRdSystemId(''); }}
          >
            View
          </button>
        </div>

        {/* RD > Start */}
        {ndRdL2 === 'start' && <div>
          <div className="nd-srd-sysid-row">
            <label className="nd-srd-sysid-label">Device System ID</label>
            <input
              className="nd-srd-sysid-input"
              type="text"
              value={rdSystemId}
              onChange={(e) => setRdSystemId(e.target.value)}
              placeholder="e.g. QC-DEV-XYZ-12345"
            />
          </div>
          <RdQuickStart systemId={rdSystemId} userInfo={userInfo} role={role} variant="full" onViewSessions={() => setNdRdL2('view')} />
          <div style={{ margin: '28px 0 0', borderTop: '1.5px dashed #e2e8f0' }} />
          <div className="nd-rd-form-wrap">
            <StartRemoteDebugTab
              userInfo={userInfo}
              prefilledSystemId={rdSystemId}
              hideSystemId
            />
          </div>
        </div>}{/* end RD Start */}

        {/* RD > View */}
        {ndRdL2 === 'view' && <div>
          <NdRdViewSessions
            userInfo={userInfo}
            role={role}
            userGroups={userGroups}
            onStartSession={() => setNdRdL2('start')}
            prefilledSearch={rdSystemId}
          />
        </div>}{/* end RD View */}
      </div>}{/* end RD */}

      {/* ══════════════════════════════════════════════
          RT — Level-2 nav + Start / View
      ══════════════════════════════════════════════ */}
      {ndL1 === 'rt' && <div>
        <div className="nd-l2-bar">
          <button
            className={`nd-l2-btn${ndRtL2 === 'start' ? ' active' : ''}`}
            onClick={() => { setNdRtL2('start'); setRtSystemId(''); }}
          >
            Start
          </button>
          <button
            className={`nd-l2-btn${ndRtL2 === 'view' ? ' active' : ''}`}
            onClick={() => { setNdRtL2('view'); setRtSystemId(''); }}
          >
            View
          </button>
        </div>

        {/* RT > Start */}
        {ndRtL2 === 'start' && <div>
          <div className="nd-srd-sysid-row">
            <label className="nd-srd-sysid-label">Device System ID</label>
            <input
              className="nd-srd-sysid-input"
              type="text"
              value={rtSystemId}
              onChange={(e) => setRtSystemId(e.target.value)}
              placeholder="e.g. QC-DEV-XYZ-12345"
            />
          </div>
          <RtQuickStart systemId={rtSystemId} userInfo={userInfo} role={role} variant="full" onViewSessions={() => setNdRtL2('view')} />
          <div style={{ margin: '28px 0 0', borderTop: '1.5px dashed #e2e8f0' }} />
          <div className="nd-rt-form-wrap">
            <RTStartSession
              userInfo={userInfo}
              prefilledSystemId={rtSystemId}
              hideSystemId
            />
          </div>
        </div>}{/* end RT Start */}

        {/* RT > View */}
        {ndRtL2 === 'view' && <div>
          <NdRtViewSessions
            userInfo={userInfo}
            onStartSession={() => setNdRtL2('start')}
            prefilledSearch={rtSystemId}
          />
        </div>}{/* end RT View */}
      </div>}{/* end RT */}

    </div>
  );
}

export default NewDiagnostics;
